<template>
    <div class="card">
        <div class="card__avatar">
            <common-rating-avatar
                :rating="physician.professionalRating"
                :user="physician"
                :size="avatarSize"
                :isUserProfileLinkEnabled="isUserProfileLinkEnabled"
            ></common-rating-avatar>

            <p v-if="physician.professionalRating" :class="ratingClass">
                <common-info-tip :showIcon="false">
                    <template #content>
                        {{ (physician.professionalRating || 0).toFixed(1) }}
                    </template>
                    <template>
                        {{ $t('elements.home.rating.heading.professional.info') }}
                    </template>
                </common-info-tip>
            </p>

            <div v-if="showNewUserIcon" class="card__new-icon">
                {{ $t('elements.referrals.card.new') }}
            </div>
        </div>

        <p :class="cardNameClass">
            <common-user-name :user="physician"></common-user-name>
        </p>

        <p class="card__role" :class="`card__role--${size}`">
            {{ showMasterUserAuthorities ? $get(physician, 'userRole.name') : $get(physician, 'practice.name') }}
        </p>

        <p v-if="bottomText" :class="`card__role--${size}`">
            {{ bottomText }}
        </p>

        <div v-if="showSpeciality" class="card__specialities">
            <common-specialities
                :specialities="physician?.specialities || []"
                :showSpecialityCount="showSpecialityCount"
            ></common-specialities>
        </div>

        <common-icon-info v-if="showEmail" class="card__email" :href="`mailto:${physician.email}`" icon="email" size="huge">
            {{ physician.email }}
        </common-icon-info>

        <div v-if="!isUserJoined && invitedDate" class="card__invited-date">
            {{ $t('elements.referrals.card.invited_on', { date: invitedDate }) }}
        </div>

        <common-buttons v-if="!isUserJoined && invitedDate" class="card__reinvite">
            <common-button
                :label="$t('elements.referrals.card.buttons.reinvite')"
                :isProcessing="isProcessingReinvite"
                size="medium-small"
                type="secondary"
                @click="reinviteUser(physician)"
            ></common-button>
        </common-buttons>
        <div v-if="physician.rating" :class="medmatchRatingClass">
            <common-rating-stars :rating="physician.rating"></common-rating-stars>
        </div>

        <p v-if="physician.referralsReceivedCount && showRefCount" class="card__referrals-count">
            <b>{{ physician.referralsReceivedCount }}</b>
            <span>{{ $t('elements.referrals.card.referrals') }}</span>
        </p>
        <slot></slot>
    </div>
</template>

<script>
    import dayjs from 'dayjs'
    import UserRolesEnum from '../../../enums/UserRolesEnum'
    import UsersAPI from 'api/UsersAPI'

    export default {
        props: {
            physician: { type: Object, required: true },
            size: { type: String, default: 'large' },
            showRefCount: { type: Boolean, default: true },
            showSpeciality: { type: Boolean, default: true },
            showMasterUserAuthorities: { type: Boolean, default: false },
            showInvitedDate: { type: Boolean, default: false },
            showNewUserWarning: { type: Boolean, default: false },
            showSpecialityCount: { type: Boolean, default: false },
            bottomText: { type: String, default: null },
            isUserProfileLinkEnabled: { type: Boolean, default: true },
        },

        data() {
            return {
                isProcessingReinvite: false,
            }
        },

        computed: {
            avatarSize() {
                switch (this.size) {
                    case 'medium':
                        return 'user-context'
                    default:
                        return 'member-switch'
                }
            },

            ratingClass() {
                return {
                    'card__professional-rating': true,
                    [`card__professional-rating--size-${this.size}`]: this.size,
                }
            },

            cardNameClass() {
                return {
                    card__name: true,
                    [`card__name--size-${this.size}`]: this.size,
                }
            },

            medmatchRatingClass() {
                return {
                    'card__medmatch-rating': true,
                    [`card__medmatch-rating--size-${this.size}`]: this.size,
                }
            },

            showEmail() {
                const isPhysician = this.physician.userRole?.id === UserRolesEnum.PHYSICIAN.ID
                const isNursePractitioner = this.physician.userRole?.id === UserRolesEnum.NURSE_PRACTITIONER.ID
                const isSignupNotFinished = !this.physician.isSignupFinished

                return (
                    this.showMasterUserAuthorities &&
                    ((!isPhysician && !isNursePractitioner) || (isPhysician && isNursePractitioner && isSignupNotFinished))
                )
            },

            invitedDate() {
                if (this.showInvitedDate && this.physician.target && this.physician.target.dateCreated) {
                    return dayjs(this.physician.target.dateCreated).format('MMMM D, YYYY')
                }
                return null
            },

            isUserJoined() {
                return this.physician.target && this.physician.target.dateJoined
            },

            isUserJoinedWithin24Hours() {
                return this.isUserJoined && dayjs(this.physician.target.dateJoined).isAfter(dayjs().subtract(1, 'day'))
            },

            showNewUserIcon() {
                return this.showNewUserWarning && this.isUserJoinedWithin24Hours
            },
        },

        methods: {
            reinviteUser(user) {
                this.isProcessingReinvite = true
                const toEmail = user.target.name
                const toPhone = user.phone
                const userId = this.$auth.getActiveMember().id
                if (toPhone) {
                    UsersAPI.sendReminderForJoinInvite(
                        this.$auth.getActiveMember().id,
                        user.id,
                        'referral',
                        'create-practice',
                    ).then(() => {
                        this.isProcessingReinvite = false
                    })
                } else {
                    UsersAPI.getJoinInviteLink(toEmail, userId)
                        .then((res) => {
                            window.open(
                                this.$t('native_emails.reinvite', {
                                    recepientEmail: toEmail,
                                    link: encodeURIComponent(res.link),
                                }),
                                '_blank',
                            )
                            this.isProcessingReinvite = false
                        })
                        .catch(() => (this.isProcessingReinvite = false))
                }
            },
        },
    }
</script>
